"use client";

import { useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import type { Vehicle, Location } from "@/lib/types";
import SearchFilterBlock, {
  type SearchSelection,
  type SpecFilters,
} from "./SearchFilterBlock";
import VehicleGrid, { type DateRange } from "./VehicleGrid";

interface HomeFleetProps {
  vehicles: Vehicle[];
  locations: Location[];
  blurMap?: Record<string, string | undefined>;
  versionMap?: Record<string, number>;
  assetBaseUrl: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const HHMM = /^\d{2}:\d{2}$/;

function parseInitial(searchParams: URLSearchParams, locations: Location[]): SearchSelection {
  const from = searchParams.get("from");
  const to = searchParams.get("to");
  const pickup_time = searchParams.get("pickup_time");
  const return_time = searchParams.get("return_time");
  const pickup_id = Number(searchParams.get("pickup_location_id"));
  const return_id = Number(searchParams.get("return_location_id"));
  const validPickup = Number.isInteger(pickup_id) && locations.some((l) => l.id === pickup_id);
  const validReturn = Number.isInteger(return_id) && locations.some((l) => l.id === return_id);

  return {
    date_from: from && ISO_DATE.test(from) ? from : null,
    date_to: to && ISO_DATE.test(to) ? to : null,
    pickup_time: pickup_time && HHMM.test(pickup_time) ? pickup_time : "12:00",
    return_time: return_time && HHMM.test(return_time) ? return_time : "12:00",
    pickup_location_id: validPickup ? pickup_id : (locations[0]?.id ?? null),
    return_location_id: validReturn ? return_id : null,
  };
}

function selectionKey(s: SearchSelection): string {
  return [s.date_from, s.date_to, s.pickup_time, s.return_time, s.pickup_location_id, s.return_location_id].join("|");
}

export default function HomeFleet({ vehicles, locations, blurMap, versionMap, assetBaseUrl }: HomeFleetProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const selection = useMemo(
    () => parseInitial(new URLSearchParams(searchParams.toString()), locations),
    [searchParams, locations],
  );

  const [specFilters, setSpecFilters] = useState<SpecFilters>({ transmission: null, body_type: null });

  function handleSearch(next: SearchSelection) {
    const params = new URLSearchParams();
    if (next.date_from) params.set("from", next.date_from);
    if (next.date_to) params.set("to", next.date_to);
    params.set("pickup_time", next.pickup_time);
    params.set("return_time", next.return_time);
    if (next.pickup_location_id != null) params.set("pickup_location_id", String(next.pickup_location_id));
    if (next.return_location_id != null) params.set("return_location_id", String(next.return_location_id));
    router.replace(`/?${params.toString()}`, { scroll: false });
  }

  const dateRange: DateRange | null =
    selection.date_from && selection.date_to
      ? { from: selection.date_from, to: selection.date_to }
      : null;

  return (
    <>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 sm:-mt-20 relative z-10">
        <SearchFilterBlock
          key={selectionKey(selection)}
          locations={locations}
          vehicles={vehicles}
          initial={selection}
          specFilters={specFilters}
          onSearch={handleSearch}
          onSpecChange={setSpecFilters}
        />
      </div>

      <VehicleGrid
        vehicles={vehicles}
        blurMap={blurMap}
        versionMap={versionMap}
        assetBaseUrl={assetBaseUrl}
        dateRange={dateRange}
        specFilters={specFilters}
        carry={{
          pickup_time: selection.pickup_time,
          return_time: selection.return_time,
          pickup_location_id: selection.pickup_location_id,
          return_location_id: selection.return_location_id,
        }}
      />
    </>
  );
}
